import axios from 'axios'
import type { CartItemSailParam, RequestDTO } from '@/root/shared-providers/BookingCart/types'
import type { Reservation } from '@/root/shared-types'
import type { PassengerFormData } from '@/root/shared-types/selectTicketPage/selectTicket'

/** The backend error code of a failed reservation request. */
export const getApiCode = (error: unknown): string | undefined =>
  axios.isAxiosError(error) ? (error.response?.data as { code?: string })?.code : undefined

/** The request with the passenger form data (or the guest's personal code) filled into each passenger slot. */
export const buildEnrichedRequest = (
  request: RequestDTO,
  formData: Record<string, Record<number, PassengerFormData>>,
  reservation?: Reservation
): RequestDTO => {
  if (!request.passengerTickets) return request
  const guestsBySeqN = reservation?.guests ? new Map(reservation.guests.map((guest) => [guest.seqN, guest])) : undefined
  return {
    ...request,
    passengerTickets: request.passengerTickets.map((ticket) => {
      const formsByKey = formData[ticket.priceCategory] ?? {}
      return {
        ...ticket,
        passengers: ticket.passengers.map((passenger, index) => {
          if ('personalIdentificationNumber' in passenger) return passenger

          // A form saved under the guest's seqN wins; otherwise the form of the slot at the same position
          const formBySeqN = formsByKey[passenger.seqN]
          const form = (formBySeqN?.seqN != null ? formBySeqN : undefined) ?? formsByKey[index]
          if (!form) {
            const guest = guestsBySeqN?.get(passenger.seqN)
            if (guest?.personalIdentificationNumber) {
              return { ...passenger, personalIdentificationNumber: guest.personalIdentificationNumber }
            }
            return passenger
          }
          return {
            seqN: passenger.seqN,
            firstName: form.firstName,
            lastName: form.lastName,
            ...(form.gender && { gender: form.gender }),
            birthday: form.birthdate,
            ...(form.citizenship && { citizenship: form.citizenship }),
            ...(form.personalId && { personalIdentificationNumber: form.personalId }),
            ...(form.additionalInfo && { comments: form.additionalInfo }),
          }
        }),
      }
    }),
  }
}

/** The request list a ticket type lives in, and the field of its slots. */
export const TICKET_SLOTS = { passengerTickets: 'passengers', vehicleTickets: 'vehicles' } as const
export type TicketList = keyof typeof TICKET_SLOTS

type Slot = { seqN: number }
// The shape both ticket lists share; a ticket holds either passengers or vehicles
type Ticket = {
  priceCategory: string
  quantity: number
  sailPackages: CartItemSailParam[]
  passengers?: Slot[]
  vehicles?: Slot[]
}

const slotsOf = (ticket: Ticket, list: TicketList) => ticket[TICKET_SLOTS[list]] ?? []

/** Adds slots to the category's ticket, or a new ticket for the category. */
export const addTickets = (
  request: RequestDTO,
  list: TicketList,
  code: string,
  slots: Slot[],
  sailPackages: CartItemSailParam[]
): RequestDTO => {
  const tickets: Ticket[] = request[list] ?? []
  const existing = tickets.find((ticket) => ticket.priceCategory === code)
  const updated = existing
    ? tickets.map((ticket) =>
        ticket.priceCategory === code
          ? {
              ...ticket,
              quantity: ticket.quantity + slots.length,
              [TICKET_SLOTS[list]]: [...slotsOf(ticket, list), ...slots],
            }
          : ticket
      )
    : [...tickets, { quantity: slots.length, priceCategory: code, sailPackages, [TICKET_SLOTS[list]]: slots }]

  return { ...request, [list]: updated }
}

/** Drops the category's last slot, or its whole ticket when one is left; undefined when the category has no ticket. */
export const removeLastTicket = (request: RequestDTO, list: TicketList, code: string): RequestDTO | undefined => {
  const tickets: Ticket[] = request[list] ?? []
  const existing = tickets.find((ticket) => ticket.priceCategory === code)
  if (!existing) return undefined

  const updated =
    existing.quantity === 1
      ? tickets.filter((ticket) => ticket.priceCategory !== code)
      : tickets.map((ticket) =>
          ticket.priceCategory === code
            ? { ...ticket, quantity: ticket.quantity - 1, [TICKET_SLOTS[list]]: slotsOf(ticket, list).slice(0, -1) }
            : ticket
        )

  return { ...request, [list]: updated }
}

/** Keeps the category's first `quantity` slots, dropping the ticket at zero. */
export const truncateTickets = (request: RequestDTO, list: TicketList, code: string, quantity: number): RequestDTO => {
  const tickets: Ticket[] = request[list] ?? []
  const updated =
    quantity <= 0
      ? tickets.filter((ticket) => ticket.priceCategory !== code)
      : tickets.map((ticket) =>
          ticket.priceCategory === code
            ? { ...ticket, quantity, [TICKET_SLOTS[list]]: slotsOf(ticket, list).slice(0, quantity) }
            : ticket
        )

  return { ...request, [list]: updated }
}

/** Removes the vehicle with the seqN from its ticket, and the ticket when it has no vehicle left. */
export const removeVehicle = (request: RequestDTO, seqN: number): RequestDTO => {
  const tickets = request.vehicleTickets ?? []
  if (!tickets.some((ticket) => ticket.vehicles.some((vehicle) => vehicle.seqN === seqN))) return request

  const updated = tickets
    .map((ticket) =>
      ticket.vehicles.some((vehicle) => vehicle.seqN === seqN)
        ? { ...ticket, quantity: ticket.quantity - 1, vehicles: ticket.vehicles.filter((v) => v.seqN !== seqN) }
        : ticket
    )
    .filter((ticket) => ticket.quantity > 0)

  return { ...request, vehicleTickets: updated }
}

/** New slots with the client seqNs that follow the last one used. */
export const newSlots = (count: number, nextSeqNum: number): Slot[] =>
  Array.from({ length: count }, (_, index) => ({ seqN: nextSeqNum + index + 1 }))
